/**
 * Phone normalization helpers.
 *
 * Storage format matches what the server expects: international digits with no
 * '+', no separators, and no local trunk '0' — e.g. "972541234567".
 * The UI shows the local number (e.g. "0541234567") + a country selector, but the
 * value written to docRef is always the normalized international form.
 */

import { COUNTRY_CODES, DEFAULT_COUNTRY_ISO } from "../data/countryCodes.js";

/**
 * Build the stored value from a local input + selected dial code.
 * @param {string} input - what the user typed (may contain spaces/dashes/leading 0)
 * @param {string} dialCode - the selected country's dial code, e.g. "972"
 * @returns {string} normalized digits ("972541234567") or "" when empty
 */
export function normalizePhone(input, dialCode) {
  let digits = String(input == null ? "" : input).replace(/\D/g, "");
  if (!digits) return "";
  const dc = String(dialCode || "");
  // Already international (user pasted a full number) — keep as-is.
  if (dc && digits.startsWith(dc)) return digits;
  // Drop the local trunk prefix (leading zeros) before prepending the dial code.
  digits = digits.replace(/^0+/, "");
  if (!digits) return "";
  return dc + digits;
}

/**
 * Split a stored international value back into { iso, dialCode, local } for display.
 * Picks the longest matching dial-code prefix; falls back to the default country.
 * @param {string} stored - normalized value, e.g. "972541234567"
 * @param {string} [defaultIso]
 */
export function parsePhone(stored, defaultIso = DEFAULT_COUNTRY_ISO) {
  const digits = String(stored == null ? "" : stored).replace(/\D/g, "");
  if (digits) {
    let best = null;
    for (const c of COUNTRY_CODES) {
      if (digits.startsWith(c.dialCode) && (!best || c.dialCode.length > best.dialCode.length)) {
        best = c;
      }
    }
    if (best) {
      return { iso: best.iso, dialCode: best.dialCode, local: digits.slice(best.dialCode.length) };
    }
  }
  const def = COUNTRY_CODES.find((c) => c.iso === defaultIso) || COUNTRY_CODES[0];
  return { iso: def.iso, dialCode: def.dialCode, local: digits };
}
